import React, { useState, useEffect } from 'react';
import contractService from '../../services/contractService';
import LoadingSpinner from '../common/LoadingSpinner';
import DailyStatesTab from './DailyStatesTab'; // AJOUT: Import du nouveau composant
import { formatAmount, formatDate } from '../../utils/formatters';
import { 
  BarChart, 
  Bar, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  ResponsiveContainer, 
  PieChart, 
  Pie, 
  Cell, 
  //LineChart,
  Line,
  AreaChart,
  Area,
  Legend
} from 'recharts';

// Palette de couleurs pour les graphiques
//const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884d8', '#82ca9d', '#ffc658', '#ff7c7c'];

// Composant pour les cartes de métriques
const MetricCard = ({ title, value, subValue, icon, color = 'blue', trend = null }) => (
  <div className={`bg-${color}-50 border border-${color}-200 rounded-lg p-4`}>
    <div className="flex items-center justify-between">
      <div>
        <p className={`text-${color}-600 text-sm font-medium`}>{title}</p>
        <p className="text-2xl font-bold text-gray-900">{value}</p>
        {subValue && <p className={`text-${color}-600 text-sm`}>{subValue}</p>}
      </div>
      <div className={`text-${color}-600 text-3xl`}>{icon}</div>
    </div>
    {trend && (
      <div className={`mt-2 text-sm ${trend.type === 'up' ? 'text-green-600' : trend.type === 'down' ? 'text-red-600' : 'text-gray-600'}`}>
        {trend.type === 'up' ? '↗️' : trend.type === 'down' ? '↘️' : '→'} {trend.value}
      </div>
    )}
  </div>
);

// Composant pour l'indicateur de pourcentage avec barre de progression
// Composant pour l'indicateur de pourcentage avec barre de progression - VERSION CORRIGÉE
const PercentageIndicator = ({ label, percentage, color = 'blue' }) => {
  // Définir les couleurs en dur pour éviter les problèmes de compilation Tailwind
  const getColorStyle = (colorName) => {
    const colors = {
      blue: '#2563eb',
      green: '#16a34a', 
      red: '#dc2626',
      purple: '#9333ea',
      orange: '#ea580c',
      yellow: '#ca8a04',
      gray: '#6b7280'
    };
    return colors[colorName] || colors.blue;
  };

  return (
    <div className="space-y-2">
      <div className="flex justify-between text-sm">
        <span className="text-gray-600">{label}</span>
        <span className="font-medium">{percentage}%</span>
      </div>
      <div className="w-full bg-gray-200 rounded-full h-2">
        <div 
          className="h-2 rounded-full transition-all duration-500"
          style={{ 
            width: `${Math.min(percentage, 100)}%`,
            backgroundColor: getColorStyle(color)
          }}
        ></div>
      </div>
    </div>
  );
};

// Composant Heatmap pour la distribution jour/agence
// Remplacer le composant AgencyDayHeatmap par celui-ci :

// Composant de graphique de répartition par jour/agence
const AgencyDayDistribution = ({ data }) => {
  if (!data || data.length === 0) {
    return <div className="text-center text-gray-500 py-8">Aucune donnée disponible pour la répartition</div>;
  }

  // Regrouper les données par agence
  const agencyData = {};
  data.forEach(item => {
    if (!agencyData[item.agency]) {
      agencyData[item.agency] = [];
    }
    agencyData[item.agency].push({
      day: item.day,
      contracts: item.contracts
    });
  });

  // Pour chaque agence, trouver les 5 jours les plus actifs
  const topDaysByAgency = {};
  Object.keys(agencyData).forEach(agency => {
    const sortedDays = [...agencyData[agency]].sort((a, b) => b.contracts - a.contracts);
    topDaysByAgency[agency] = sortedDays.slice(0, 5); // Prendre les 5 premiers
  });

  // Préparer les données pour le graphique
  const chartData = Object.keys(topDaysByAgency).map(agency => {
    const agencyResult = { agency };
    topDaysByAgency[agency].forEach(dayData => {
      agencyResult[`Jour ${dayData.day}`] = dayData.contracts;
    });
    return agencyResult;
  });

  // Limiter à 8 agences maximum pour la lisibilité
  const limitedChartData = chartData.slice(0, 8);

  // Obtenir tous les jours uniques pour les barres
  const allDays = new Set();
  Object.values(topDaysByAgency).forEach(days => {
    days.forEach(day => allDays.add(`Jour ${day.day}`));
  });
  const uniqueDays = Array.from(allDays);

  // Générer les couleurs pour les barres
  const colors = [
    '#8884d8', '#82ca9d', '#ffc658', '#ff8042', '#0088FE', 
    '#00C49F', '#FFBB28', '#FF8042', '#8884d8'
  ];

  return (
    <div className="space-y-4">
      <h4 className="text-lg font-semibold">Jours les plus actifs par agence</h4>
      <p className="text-sm text-gray-600">Pour chaque agence, les 5 jours avec le plus de contrats créés</p>
      
      <div className="h-80">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart 
            data={limitedChartData}
            margin={{ top: 20, right: 30, left: 20, bottom: 70 }}
          >
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis 
              dataKey="agency" 
              angle={-45} 
              textAnchor="end" 
              height={70}
              interval={0}
            />
            <YAxis />
            <Tooltip />
            <Legend />
            {uniqueDays.map((day, index) => (
              <Bar 
                key={day} 
                dataKey={day} 
                name={day} 
                fill={colors[index % colors.length]} 
                stackId="stack"
              />
            ))}
          </BarChart>
        </ResponsiveContainer>
      </div>

      {/* Tableau récapitulatif pour toutes les agences */}
      <div className="mt-6">
        <h4 className="text-md font-medium mb-2">Détail des jours les plus actifs</h4>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th scope="col" className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Agence
                </th>
                <th scope="col" className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Jour le plus actif
                </th>
                <th scope="col" className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Contrats
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {Object.keys(topDaysByAgency).map(agency => {
                const topDay = topDaysByAgency[agency][0] || { day: 0, contracts: 0 };
                return (
                  <tr key={agency}>
                    <td className="px-4 py-2 whitespace-nowrap text-sm font-medium text-gray-900">
                      {agency}
                    </td>
                    <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-500">
                      Jour {topDay.day}
                    </td>
                    <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-500 text-right">
                      {topDay.contracts}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

const ContractStatisticsModal = ({ isOpen, onClose, filters }) => {
  const [statistics, setStatistics] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');

  // MODIFICATION: Liste des onglets avec le nouvel onglet États journaliers
  const tabs = [
    { id: 'overview', label: 'Vue d\'ensemble', icon: '📊' },
    { id: 'financial', label: 'Analyse financière', icon: '💰' },
    { id: 'temporal', label: 'Analyse temporelle', icon: '📅' },
    { id: 'agency', label: 'Par agence', icon: '🏢' },
    { id: 'performance', label: 'Performance', icon: '📈' },
    { id: 'alerts', label: 'Alertes', icon: '⚠️' },
    { id: 'daily-states', label: 'États journaliers', icon: '📋' } // AJOUT: Nouvel onglet
  ];

  // Fonction pour formater les pourcentages
  const formatPercentage = (value) => {
    if (value === null || value === undefined) return '0,00%';
    return `${Number(parseFloat(value).toFixed(2)).toLocaleString('fr-FR', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}%`;
  };

  // Charger les statistiques
  const loadStatistics = async () => {
    if (!isOpen) return;

    try {
      setLoading(true);
      setError(null);
      
      console.log('Chargement des statistiques avec filtres:', filters);
      const result = await contractService.getStatistics(filters);
      
      if (result.success) {
        setStatistics(result.data);
        console.log('Statistiques chargées:', result.data);
      } else {
        setError(result.message || 'Erreur lors du chargement des statistiques');
      }
    } catch (error) {
      console.error('Erreur lors du chargement des statistiques:', error);
      setError('Erreur lors du chargement des statistiques');
    } finally {
      setLoading(false);
    }
  };
  // Charger les statistiques quand la modal s'ouvre ou que les filtres changent
 useEffect(() => {
   if (isOpen) {
     loadStatistics();
   }
   // eslint-disable-next-line react-hooks/exhaustive-deps
 }, [isOpen, filters]);

 // Gérer la fermeture avec la touche Échap
 useEffect(() => {
   const handleEsc = (event) => {
     if (event.keyCode === 27) {
       onClose();
     }
   };
   
   if (isOpen) {
     document.addEventListener('keydown', handleEsc);
     document.body.style.overflow = 'hidden';
   }
   
   return () => {
     document.removeEventListener('keydown', handleEsc);
     document.body.style.overflow = 'auto';
   };
 }, [isOpen, onClose]);

 // Réinitialiser l'onglet actif quand la modal s'ouvre
 useEffect(() => {
   if (isOpen) {
     setActiveTab('overview');
   }
 }, [isOpen]);

 if (!isOpen) return null;

 // Fonction pour rendre l'onglet Vue d'ensemble
 const renderOverviewTab = () => (
   <div className="space-y-6">
     <h3 className="text-xl font-semibold mb-4">Vue d'ensemble</h3>
     
     {/* Métriques de base */}
     <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
       <MetricCard
         title="Total des contrats"
         value={statistics.basicMetrics.totalContracts.toLocaleString('fr-FR')}
         icon="📋"
         color="blue"
       />
       <MetricCard
         title="Agences représentées"
         value={statistics.basicMetrics.agenciesCount}
         subValue={`sur ${statistics.basicMetrics.uniqueAgencies?.length || 0} agences`}
         icon="🏢"
         color="green"
       />
       <MetricCard
         title="Période analysée"
         value={statistics.basicMetrics.dateRange.min && statistics.basicMetrics.dateRange.max 
           ? `${formatDate(statistics.basicMetrics.dateRange.min)} - ${formatDate(statistics.basicMetrics.dateRange.max)}`
           : 'N/A'
         }
         icon="📅"
         color="purple"
       />
     </div>

     {/* Résumé financier rapide */}
     <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
       <MetricCard
         title="Montant Prévu Total"
         value={formatAmount(statistics.financialStats.totals.expectedAmount)}
         icon="💰"
         color="yellow"
       />
       <MetricCard
         title="Montant Perçu Total"
         value={formatAmount(statistics.financialStats.totals.receivedAmount)}
         icon="✅"
         color="green"
       />
       <MetricCard
         title="Taux de Recouvrement"
         value={formatPercentage(statistics.financialStats.ratios.recoveryRate)}
         icon="📊"
         color="blue"
       />
       <MetricCard
         title="Contrats Soldés"
         value={formatPercentage(statistics.financialStats.ratios.paidContractsPercentage)}
         icon="🎯"
         color="green"
       />
     </div>

     {/* MODIFICATION: Aperçu des alertes corrigé */}
     <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
       <h4 className="text-lg font-semibold text-yellow-800 mb-3">⚠️ Points d'attention</h4>
       <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
         <div className="text-center">
           <div className="text-2xl font-bold text-red-600">{statistics.alerts.contractsNotClosed}</div>
           <div className="text-red-600">Contrats non clos</div>
           <div className="text-xs text-gray-600">Date clôture vide</div>
         </div>
         <div className="text-center">
           <div className="text-2xl font-bold text-orange-600">{statistics.alerts.contractsToWatch}</div>
           <div className="text-orange-600">À surveiller</div>
           <div className="text-xs text-gray-600">État = "A surveiller"</div>
         </div>
       </div>
     </div>
   </div>
 );

 // MODIFICATION: Fonction pour rendre l'onglet Analyse financière avec graphique corrigé
 const renderFinancialTab = () => (
   <div className="space-y-6">
     <h3 className="text-xl font-semibold mb-4">Analyse financière</h3>
     
     {/* Totaux financiers */}
     <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
       <MetricCard
         title="Montant Décaissé"
         value={formatAmount(statistics.financialStats.totals.disbursementAmount)}
         icon="💸"
         color="red"
       />
       <MetricCard
         title="Montant Prévu"
         value={formatAmount(statistics.financialStats.totals.expectedAmount)}
         icon="🎯"
         color="blue"
       />
       <MetricCard
         title="Montant Perçu"
         value={formatAmount(statistics.financialStats.totals.receivedAmount)}
         icon="✅"
         color="green"
       />
       <MetricCard
         title="Reste à Percevoir"
         value={formatAmount(statistics.financialStats.totals.remainingAmount)}
         icon="⏳"
         color="orange"
       />
       <MetricCard
         title="Total Remises"
         value={formatAmount(statistics.financialStats.totals.discount)}
         icon="🎁"
         color="purple"
       />
     </div>

     <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
       {/* Ratios et indicateurs */}
       <div className="bg-white border border-gray-200 rounded-lg p-4">
         <h4 className="text-lg font-semibold mb-4">Indicateurs Financiers</h4>
         <div className="space-y-4">
           <PercentageIndicator
             label="Taux de Recouvrement"
             percentage={statistics.financialStats.ratios.recoveryRate}
             color="green"
           />
           <PercentageIndicator
             label="Contrats Soldés"
             percentage={statistics.financialStats.ratios.paidContractsPercentage}
             color="blue"
           />
           <PercentageIndicator
             label="Contrats en Retard"
             percentage={statistics.financialStats.ratios.overdueContractsPercentage}
             color="red"
           />
         </div>
         <div className="mt-4 pt-4 border-t">
           <div className="text-center">
             <div className="text-sm text-gray-600">Montant moyen par contrat</div>
             <div className="text-xl font-bold text-blue-600">
               {formatAmount(statistics.financialStats.averages.averagePerContract)}
             </div>
           </div>
         </div>
       </div>

       {/* CORRECTION: Graphique camembert répartition avec données fixes */}
       <div className="bg-white border border-gray-200 rounded-lg p-4">
         <h4 className="text-lg font-semibold mb-4">Répartition des Montants</h4>
         <ResponsiveContainer width="100%" height={300}>
           <PieChart>
             <Pie
               data={[
                 { 
                   name: 'Montant Perçu', 
                   value: statistics.financialStats.totals.receivedAmount, 
                   fill: '#00C49F' 
                 },
                 { 
                   name: 'Reste à Percevoir', 
                   value: Math.max(0, statistics.financialStats.totals.remainingAmount), 
                   fill: '#FFBB28' 
                 },
                 { 
                   name: 'Remises', 
                   value: statistics.financialStats.totals.discount, 
                   fill: '#FF8042' 
                 }
               ]}
               cx="50%"
               cy="50%"
               outerRadius={100}
               dataKey="value"
               label={({name, percent}) => `${name}: ${(percent * 100).toFixed(1)}%`}
             >
               {[0, 1, 2].map((entry, index) => (
                 <Cell key={`cell-${index}`} fill={[0, 1, 2].map(i => ['#00C49F', '#FFBB28', '#FF8042'][i])[index]} />
               ))}
             </Pie>
             <Tooltip formatter={(value) => formatAmount(value)} />
           </PieChart>
         </ResponsiveContainer>
       </div>
     </div>
   </div>
 );

 // MODIFICATION: Fonction pour rendre l'onglet Analyse temporelle avec nouveau graphique
 const renderTemporalTab = () => (
   <div className="space-y-6">
     <h3 className="text-xl font-semibold mb-4">Analyse temporelle</h3>
     
     {/* Métrique du délai moyen */}
     <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
       <MetricCard
         title="Délai Moyen de Remboursement"
         value={`${statistics.temporalStats.averageRepaymentDelay} jours`}
         icon="⏱️"
         color="blue"
       />
       <MetricCard
         title="Périodes Analysées"
         value={statistics.temporalStats.monthlyDistribution?.length || 0}
         subValue="mois"
         icon="📅"
         color="green"
       />
     </div>

     {/* AJOUT: Graphique par jour/agence si un mois spécifique est sélectionné */}
     {statistics.temporalStats.hasSpecificMonth && statistics.temporalStats.contractsByDayAndAgency && statistics.temporalStats.contractsByDayAndAgency.length > 0 && (
       <div className="bg-white border border-gray-200 rounded-lg p-4">
         <h4 className="text-lg font-semibold mb-4">Répartition par Jour et Agence</h4>
         <ResponsiveContainer width="100%" height={300}>
           <BarChart data={statistics.temporalStats.contractsByDayAndAgency}>
             <CartesianGrid strokeDasharray="3 3" />
             <XAxis 
               dataKey="day" 
               label={{ value: 'Jour du mois', position: 'insideBottom', offset: -5 }}
             />
             <YAxis 
               label={{ value: 'Nombre de contrats', angle: -90, position: 'insideLeft' }}
             />
             <Tooltip 
               formatter={(value, name) => [value, 'Contrats']}
               labelFormatter={(label) => `Jour ${label}`}
             />
             <Bar dataKey="contracts" fill="#8884d8" />
           </BarChart>
         </ResponsiveContainer>
       </div>
     )}

     {/* Graphique de l'évolution mensuelle (visible seulement si pas de mois spécifique) */}
     {!statistics.temporalStats.hasSpecificMonth && statistics.temporalStats.monthlyDistribution && statistics.temporalStats.monthlyDistribution.length > 0 && (
       <div className="bg-white border border-gray-200 rounded-lg p-4">
         <h4 className="text-lg font-semibold mb-4">Évolution Mensuelle des Contrats</h4>
         <ResponsiveContainer width="100%" height={300}>
           <AreaChart data={statistics.temporalStats.monthlyDistribution}>
             <CartesianGrid strokeDasharray="3 3" />
             <XAxis dataKey="period" />
             <YAxis yAxisId="contracts" orientation="left" />
             <YAxis yAxisId="amount" orientation="right" />
             <Tooltip 
               formatter={(value, name) => [
                 name === 'contracts' ? value : formatAmount(value),
                 name === 'contracts' ? 'Contrats' : 'Montant Total'
               ]}
             />
             <Area 
               yAxisId="contracts"
               type="monotone" 
               dataKey="contracts" 
               stroke="#8884d8" 
               fillOpacity={0.6}
               fill="#8884d8"
               name="contracts"
             />
             <Line 
               yAxisId="amount"
               type="monotone" 
               dataKey="totalAmount" 
               stroke="#82ca9d"
               strokeWidth={3}
               name="totalAmount"
             />
           </AreaChart>
         </ResponsiveContainer>
       </div>
     )}
   </div>
 );

 // CORRECTION: Fonction pour rendre l'onglet Par agence avec données corrigées
 const renderAgencyTab = () => (
   <div className="space-y-6">
     <h3 className="text-xl font-semibold mb-4">Analyse par agence</h3>
     
     {/* Graphique des contrats par agence */}
     {statistics.agencyStats.distributionByAgency && statistics.agencyStats.distributionByAgency.length > 0 && (
       <div className="bg-white border border-gray-200 rounded-lg p-4">
         <h4 className="text-lg font-semibold mb-4">Répartition des contrats par agence</h4>
         <ResponsiveContainer width="100%" height={300}>
           <BarChart data={statistics.agencyStats.distributionByAgency}>
             <CartesianGrid strokeDasharray="3 3" />
             <XAxis dataKey="agency" />
             <YAxis />
             <Tooltip />
             <Bar dataKey="contracts" fill="#8884d8" />
           </BarChart>
         </ResponsiveContainer>
       </div>
     )}

     {/* Graphique des montants par agence */}
     {statistics.agencyStats.distributionByAgency && statistics.agencyStats.distributionByAgency.length > 0 && (
       <div className="bg-white border border-gray-200 rounded-lg p-4">
         <h4 className="text-lg font-semibold mb-4">Répartition des montants par agence</h4>
         <ResponsiveContainer width="100%" height={300}>
           <BarChart data={statistics.agencyStats.distributionByAgency}>
             <CartesianGrid strokeDasharray="3 3" />
             <XAxis dataKey="agency" />
             <YAxis />
             <Tooltip formatter={(value) => formatAmount(value)} />
             <Bar dataKey="totalAmount" fill="#82ca9d" />
           </BarChart>
         </ResponsiveContainer>
       </div>
     )}


     {/* Heatmap agence/jour */}
{/* Distribution par jour et agence */}
{statistics.agencyStats.dayAgencyHeatmap && statistics.agencyStats.dayAgencyHeatmap.length > 0 && (
  <div className="bg-white border border-gray-200 rounded-lg p-4">
    <AgencyDayDistribution data={statistics.agencyStats.dayAgencyHeatmap} />
  </div>
)}




     {/* Tableau détaillé par agence avec taux de renouvellement */}
     {statistics.agencyStats.renewalByAgency && statistics.agencyStats.renewalByAgency.length > 0 && (
       <div className="bg-white border border-gray-200 rounded-lg p-4">
         <h4 className="text-lg font-semibold mb-4">Détail par Agence</h4>
         <div className="overflow-x-auto">
           <table className="min-w-full table-auto">
             <thead className="bg-gray-50">
               <tr>
                 <th className="px-4 py-2 text-left">Agence</th>
                 <th className="px-4 py-2 text-right">Contrats</th>
                 <th className="px-4 py-2 text-right">Montant Total</th>
                 <th className="px-4 py-2 text-right">Poids Total</th>
                 <th className="px-4 py-2 text-right">Taux Renouvellement</th>
               </tr>
             </thead>
             <tbody>
               {statistics.agencyStats.distributionByAgency.map((agency, index) => {
                 const renewalData = statistics.agencyStats.renewalByAgency.find(r => r.agency === agency.agency);
                 return (
                   <tr key={agency.agency} className={index % 2 === 0 ? 'bg-gray-50' : 'bg-white'}>
                     <td className="px-4 py-2 font-medium">{agency.agency}</td>
                     <td className="px-4 py-2 text-right">{agency.contracts}</td>
                     <td className="px-4 py-2 text-right">{formatAmount(agency.totalAmount)}</td>
                     <td className="px-4 py-2 text-right">{agency.totalWeight.toFixed(2)}</td>
                     <td className="px-4 py-2 text-right">
                       {renewalData ? formatPercentage(renewalData.renewalPercentage) : '-'}
                     </td>
                   </tr>
                 );
               })}
             </tbody>
           </table>
         </div>
       </div>
     )}
   </div>
 );

 // CORRECTION: Fonction pour rendre l'onglet Performance avec graphique R vs NR
 const renderPerformanceTab = () => (
   <div className="space-y-6">
     <h3 className="text-xl font-semibold mb-4">Analyse de performance</h3>
     
     <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
       {/* Taux de renouvellement global */}
       <div className="bg-white border border-gray-200 rounded-lg p-4">
         <h4 className="text-lg font-semibold mb-4">Taux de Renouvellement Global</h4>
         <div className="space-y-4">
           <div className="flex justify-between">
             <span className="text-gray-600">Contrats renouvelés (R):</span>
             <span className="font-medium text-green-600">{statistics.performanceStats.renewalRate.renewed}</span>
           </div>
           <div className="flex justify-between">
             <span className="text-gray-600">Total évalué:</span>
             <span className="font-medium">{statistics.performanceStats.renewalRate.total}</span>
           </div>
           <PercentageIndicator
             label="Taux de renouvellement"
             percentage={statistics.performanceStats.renewalRate.renewalPercentage}
             color="green"
           />
         </div>
       </div>

       {/* AJOUT: Graphique camembert R vs NR */}
       <div className="bg-white border border-gray-200 rounded-lg p-4">
         <h4 className="text-lg font-semibold mb-4">Répartition R vs NR</h4>
         <ResponsiveContainer width="100%" height={250}>
           <PieChart>
             <Pie
               data={[
                 { 
                   name: 'Renouvelés (R)', 
                   value: statistics.performanceStats.renewalRate.renewed, 
                   fill: '#00C49F' 
                 },
                 { 
                   name: 'Non Renouvelés (NR)', 
                   value: statistics.performanceStats.renewalRate.notRenewed, 
                   fill: '#FF8042' 
                 }
               ]}
               cx="50%"
               cy="50%"
               outerRadius={80}
               dataKey="value"
               label={({name, percent}) => `${name}: ${(percent * 100).toFixed(1)}%`}
             >
               <Cell fill="#00C49F" />
               <Cell fill="#FF8042" />
             </Pie>
             <Tooltip formatter={(value) => value} />
           </PieChart>
         </ResponsiveContainer>
       </div>

       {/* Statistiques sur les remises */}
       <div className="bg-white border border-gray-200 rounded-lg p-4">
         <h4 className="text-lg font-semibold mb-4">Analyse des Remises</h4>
         <div className="space-y-4">
           <div className="flex justify-between">
             <span className="text-gray-600">Nombre de contrats avec remise:</span>
             <span className="font-medium">{statistics.performanceStats.discountStats.contractsWithDiscount}</span>
           </div>
           <div className="flex justify-between">
             <span className="text-gray-600">Montant total des remises:</span>
             <span className="font-medium">{formatAmount(statistics.performanceStats.discountStats.totalDiscount)}</span>
           </div>
           <div className="flex justify-between">
             <span className="text-gray-600">Remise moyenne:</span>
             <span className="font-medium">{formatAmount(statistics.performanceStats.discountStats.averageDiscount)}</span>
           </div>
           <PercentageIndicator
             label="Pourcentage de contrats avec remise"
             percentage={statistics.performanceStats.discountStats.discountPercentage}
             color="purple"
           />
         </div>
       </div>

       {/* Statistiques sur les commentaires */}
       <div className="bg-white border border-gray-200 rounded-lg p-4">
         <h4 className="text-lg font-semibold mb-4">Analyse des Commentaires</h4>
         <div className="space-y-4">
           <div className="flex justify-between">
             <span className="text-gray-600">Contrats avec commentaires:</span>
             <span className="font-medium">{statistics.performanceStats.commentsStats.contractsWithComments}</span>
           </div>
           <PercentageIndicator
             label="Pourcentage de contrats commentés"
             percentage={statistics.performanceStats.commentsStats.commentsPercentage}
             color="blue"
           />
         </div>
       </div>
     </div>

     {/* Tableau de renouvellement par agence */}
     <div className="bg-white border border-gray-200 rounded-lg p-4">
       <h4 className="text-lg font-semibold mb-4">Taux de Renouvellement par Agence</h4>
       <div className="overflow-x-auto">
         <table className="min-w-full table-auto">
           <thead className="bg-gray-50">
             <tr>
               <th className="px-4 py-2 text-left">Agence</th>
               <th className="px-4 py-2 text-right">Renouvelés</th>
               <th className="px-4 py-2 text-right">Non Renouvelés</th>
               <th className="px-4 py-2 text-right">Total</th>
               <th className="px-4 py-2 text-right">Taux</th>
             </tr>
           </thead>
           <tbody>
             {statistics.agencyStats.renewalByAgency.map((agency, index) => (
               <tr key={agency.agency} className={index % 2 === 0 ? 'bg-gray-50' : 'bg-white'}>
                 <td className="px-4 py-2 font-medium">{agency.agency}</td>
                 <td className="px-4 py-2 text-right text-green-600">{agency.renewed}</td>
                 <td className="px-4 py-2 text-right text-red-600">{agency.notRenewed}</td>
                 <td className="px-4 py-2 text-right">{agency.total}</td>
                 <td className="px-4 py-2 text-right font-medium">
                   {formatPercentage(agency.renewalPercentage)}
                 </td>
               </tr>
             ))}
           </tbody>
         </table>
       </div>
     </div>
   </div>
 );

 // CORRECTION: Fonction pour rendre l'onglet Alertes avec logiques corrigées
 const renderAlertsTab = () => (
   <div className="space-y-6">
     <h3 className="text-xl font-semibold mb-4">Alertes et points d'attention</h3>
     
     {/* MODIFICATION: Résumé des alertes corrigé */}
     <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
       <MetricCard
         title="Contrats non clos"
         value={statistics.alerts.contractsNotClosed}
         subValue="Date clôture vide"
         icon="📋"
         color="red"
       />
       <MetricCard
         title="À Surveiller"
         value={statistics.alerts.contractsToWatch}
         subValue="État = 'A surveiller'"
         icon="👁️"
         color="orange"
       />
       <MetricCard
         title="Contrats en Dépassement"
         value={statistics.alerts.overdueContracts}
         subValue="Limite dépassée & non clos"
         icon="🚨"
         color="purple"
       />
     </div>

     <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
       {/* Top 10 des contrats en dépassement d'échéance */}
       <div className="bg-white border border-red-200 rounded-lg p-4">
         <h4 className="text-lg font-semibold mb-4 text-red-800">🚨 Contrats en Dépassement d'Échéance</h4>
         {statistics.alerts.overdueDetails && statistics.alerts.overdueDetails.length > 0 ? (
           <div className="overflow-x-auto">
             <table className="min-w-full table-auto text-sm">
               <thead className="bg-red-50">
                 <tr>
                   <th className="px-3 py-2 text-left">Contrat</th>
                   <th className="px-3 py-2 text-left">Agence</th>
                   <th className="px-3 py-2 text-left">Client</th>
                   <th className="px-3 py-2 text-right">Jours de retard</th>
                 </tr>
               </thead>
               <tbody>
                 {statistics.alerts.overdueDetails.map((contract, index) => (
                   <tr key={contract.contractNumber} className={index % 2 === 0 ? 'bg-red-25' : 'bg-white'}>
                     <td className="px-3 py-2 font-medium">{contract.contractNumber}</td>
                     <td className="px-3 py-2">{contract.agency}</td>
                     <td className="px-3 py-2">{contract.client}</td>
                     <td className="px-3 py-2 text-right text-red-600 font-medium">
                       {contract.daysOverdue} jours
                     </td>
                   </tr>
                 ))}
               </tbody>
             </table>
           </div>
         ) : (
           <p className="text-gray-500 text-center py-4">Aucun contrat en dépassement 🎉</p>
         )}
       </div>

       {/* MODIFICATION: Contrats à surveiller (état = "A surveiller") */}
       <div className="bg-white border border-orange-200 rounded-lg p-4">
         <h4 className="text-lg font-semibold mb-4 text-orange-800">👁️ Contrats à Surveiller</h4>
         {statistics.alerts.watchDetails && statistics.alerts.watchDetails.length > 0 ? (
           <div className="overflow-x-auto">
             <table className="min-w-full table-auto text-sm">
               <thead className="bg-orange-50">
                 <tr>
                   <th className="px-3 py-2 text-left">Contrat</th>
                   <th className="px-3 py-2 text-left">Agence</th>
                   <th className="px-3 py-2 text-left">Client</th>
                   <th className="px-3 py-2 text-center">État</th>
                 </tr>
               </thead>
               <tbody>
                 {statistics.alerts.watchDetails.map((contract, index) => (
                   <tr key={contract.contractNumber} className={index % 2 === 0 ? 'bg-orange-25' : 'bg-white'}>
                     <td className="px-3 py-2 font-medium">{contract.contractNumber}</td>
                     <td className="px-3 py-2">{contract.agency}</td>
                     <td className="px-3 py-2">{contract.client}</td>
                     <td className="px-3 py-2 text-center">
                       <span className="bg-orange-100 text-orange-800 px-2 py-1 rounded-full text-xs">
                         {contract.etatContrat}
                       </span>
                     </td>
                   </tr>
                 ))}
               </tbody>
             </table>
           </div>
         ) : (
           <p className="text-gray-500 text-center py-4">Aucun contrat à surveiller</p>
         )}
       </div>
     </div>

     {/* Analyse des états de paiement */}
     <div className="bg-white border border-gray-200 rounded-lg p-4">
       <h4 className="text-lg font-semibold mb-4">État des Paiements</h4>
       <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="text-center p-4 bg-green-50 rounded-lg">
           <div className="text-3xl font-bold text-green-600">
             {formatPercentage(statistics.financialStats.ratios.paidContractsPercentage)}
           </div>
           <div className="text-green-600 font-medium">Contrats Soldés</div>
           <div className="text-sm text-gray-600 mt-1">
             Reste = 0
           </div>
         </div>
         <div className="text-center p-4 bg-orange-50 rounded-lg">
           <div className="text-3xl font-bold text-orange-600">
             {formatPercentage(
               100 - statistics.financialStats.ratios.paidContractsPercentage - statistics.financialStats.ratios.overdueContractsPercentage
             )}
           </div>
           <div className="text-orange-600 font-medium">En Cours</div>
           <div className="text-sm text-gray-600 mt-1">
             Reste supérieur à 0
           </div>
         </div>
         <div className="text-center p-4 bg-red-50 rounded-lg">
           <div className="text-3xl font-bold text-red-600">
             {formatPercentage(statistics.financialStats.ratios.overdueContractsPercentage)}
           </div>
           <div className="text-red-600 font-medium">Dépassement (DDGR)</div>
           <div className="text-sm text-gray-600 mt-1">
             Reste inférieur à 0
           </div>
         </div>
       </div>
     </div>
   </div>
 );

 // AJOUT: Fonction pour rendre l'onglet États journaliers
 const renderDailyStatesTab = () => (
   <DailyStatesTab initialFilters={filters} />
 );

 // Fonction pour rendre le contenu selon l'onglet actif
 const renderContent = () => {
   switch (activeTab) {
     case 'overview':
       return renderOverviewTab();
     case 'financial':
       return renderFinancialTab();
     case 'temporal':
       return renderTemporalTab();
     case 'agency':
       return renderAgencyTab();
     case 'performance':
       return renderPerformanceTab();
     case 'alerts':
       return renderAlertsTab();
     case 'daily-states': // AJOUT: Nouveau cas pour l'onglet États journaliers
       return renderDailyStatesTab();
     default:
       return <div className="text-center text-gray-500 py-8">Onglet non reconnu</div>;
   }
 };

 return (
   <div className="fixed inset-0 z-50 overflow-y-auto">
     {/* Overlay */}
     <div 
       className="fixed inset-0 bg-black bg-opacity-50 transition-opacity"
       onClick={onClose}
     ></div>

     {/* Modal */}
     <div className="flex min-h-screen items-center justify-center p-4">
       <div 
         className="relative bg-white rounded-lg shadow-xl w-full max-w-6xl max-h-[95vh] overflow-hidden"
         onClick={(e) => e.stopPropagation()}
       >
         {/* En-tête de la modal */}
         <div className="bg-gray-50 px-6 py-4 border-b border-gray-200">
           <div className="flex items-center justify-between">
             <h2 className="text-xl font-semibold text-gray-900">
               📊 Statistiques des contrats
             </h2>
             <button
               onClick={onClose}
               className="text-gray-400 hover:text-gray-600 transition-colors"
             >
               <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                 <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
               </svg>
             </button>
           </div>

           {/* Informations sur les filtres appliqués */}
           {filters && Object.keys(filters).some(key => filters[key] !== '' && filters[key] !== undefined) && (
             <div className="mt-3 text-sm text-gray-600">
               <span className="font-medium">Filtres appliqués: </span>
               {filters.agency && <span className="inline-block bg-blue-100 text-blue-800 px-2 py-1 rounded-full text-xs mr-2">Agence: {filters.agency}</span>}
               {filters.year && <span className="inline-block bg-green-100 text-green-800 px-2 py-1 rounded-full text-xs mr-2">Année: {filters.year}</span>}
               {filters.month && <span className="inline-block bg-purple-100 text-purple-800 px-2 py-1 rounded-full text-xs mr-2">Mois: {filters.month}</span>}
               {filters.search && <span className="inline-block bg-orange-100 text-orange-800 px-2 py-1 rounded-full text-xs mr-2">Recherche: "{filters.search}"</span>}
             </div>
           )}
         </div>

         {/* Navigation des onglets */}
         <div className="bg-white border-b border-gray-200">
           <nav className="flex overflow-x-auto">
             {tabs.map((tab) => (
               <button
                 key={tab.id}
                 onClick={() => setActiveTab(tab.id)}
                 className={`px-4 py-3 text-sm font-medium whitespace-nowrap flex items-center space-x-2 transition-colors ${
                   activeTab === tab.id
                     ? 'text-blue-600 border-b-2 border-blue-600 bg-blue-50'
                     : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'
                 }`}
               >
                 <span>{tab.icon}</span>
                 <span>{tab.label}</span>
               </button>
             ))}
           </nav>
         </div>

         {/* Contenu de la modal */}
         <div className="overflow-y-auto max-h-[calc(95vh-140px)]">
           {/* MODIFICATION: Condition pour afficher le loading seulement pour les onglets qui nécessitent les statistiques */}
           {(loading && activeTab !== 'daily-states') ? (
             <div className="flex items-center justify-center h-64">
               <LoadingSpinner message="Chargement des statistiques..." />
             </div>
           ) : (error && activeTab !== 'daily-states') ? (
             <div className="flex items-center justify-center h-64">
               <div className="text-center">
                 <div className="text-red-500 text-6xl mb-4">😟</div>
                 <h3 className="text-lg font-medium text-gray-900 mb-2">
                   Erreur de chargement
                 </h3>
                 <p className="text-gray-600 mb-4">{error}</p>
                 <button
                   onClick={loadStatistics}
                   className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
                 >
                   Réessayer
                 </button>
               </div>
             </div>
           ) : (statistics || activeTab === 'daily-states') ? (
             <div className="p-6">
               {renderContent()}
             </div>
           ) : (
             <div className="flex items-center justify-center h-64">
               <div className="text-center">
                 <div className="text-gray-400 text-6xl mb-4">📊</div>
                 <p className="text-gray-600">Aucune donnée disponible</p>
               </div>
             </div>
           )}
         </div>

         {/* Pied de page de la modal */}
         <div className="bg-gray-50 px-6 py-4 border-t border-gray-200 flex justify-between items-center">
           <div className="text-sm text-gray-600">
             {/* MODIFICATION: Affichage conditionnel des informations selon l'onglet */}
             {activeTab === 'daily-states' ? (
               'Onglet États journaliers'
             ) : statistics ? (
               <>
                 {statistics.basicMetrics.totalContracts} contrat(s) analysé(s)
                 {statistics.basicMetrics.dateRange.min && statistics.basicMetrics.dateRange.max && (
                   <>
                     {' du '}
                     {formatDate(statistics.basicMetrics.dateRange.min)}
                     {' au '}
                     {formatDate(statistics.basicMetrics.dateRange.max)}
                   </>
                 )}
               </>
             ) : (
               'Aucune donnée'
             )}
           </div>
           <div className="flex space-x-3">
             {/* MODIFICATION: Bouton d'actualisation conditionnel */}
             {activeTab !== 'daily-states' && (
               <button
                 onClick={loadStatistics}
                 disabled={loading}
                 className="px-4 py-2 text-blue-600 bg-white border border-blue-600 rounded-md hover:bg-blue-50 transition-colors disabled:opacity-50"
               >
                 🔄 Actualiser
               </button>
             )}
             <button
               onClick={onClose}
               className="px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700 transition-colors"
             >
               Fermer
             </button>
           </div>
         </div>
       </div>
     </div>
   </div>
 );
};

export default ContractStatisticsModal;